import React, { useState, useEffect, useRef } from "react";
import {
  Table, Button, Modal, Card, Row, Col, Space,
  Typography, Layout, Empty, Spin, Tooltip
} from "antd";
import {
  FaCalendarAlt, FaUser, FaClock,
  FaEye, FaRegCalendarAlt, FaFilePdf
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/Api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const { Title, Text } = Typography;
const { Content } = Layout;

const TurnosTrabalho = () => {
  const [meses, setMeses] = useState([]);
  const [dias, setDias] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [escalasAtividade, setEscalasAtividade] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState(null);
  const [detalhesLoading, setDetalhesLoading] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);

  useEffect(() => {
    document.title = "TURNOS DE TRABALHO";
    fetchMeses();
    fetchEscalasAtividade();
  }, []);

  const sms_success = (m) => toast.success(m);
  const sms_error   = (m) => toast.error(m);

  // -------------------------------------------------------
  // GERAR PDF com jsPDF + autoTable (captura TODOS os dados)
  const handleGerarPDF = () => {
    setGerandoPDF(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });

      // Título
      doc.setFontSize(14);
      doc.text(`Escala Mensal - ${mesSelecionado?.descricao}`, 40, 30);
      doc.setFontSize(9);
      doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")}`, 40, 45);

      const usuarios = agruparTurnosPorUsuario();

      // Cabeçalho: Colaborador + cada dia
      const head = [
        [
          "Colaborador",
          ...dias.map(dia => {
            const { dia: n, diaSemana } = formatarData(dia.dia);
            return `${diaSemana}\n${n}`;
          })
        ]
      ];

      // Linhas: nome + abreviação do turno em cada dia
      const body = usuarios.map(usuario => [
        usuario.nome,
        ...dias.map(dia => {
          const turno = obterTurnoUsuarioDia(usuario.nome, dia.id);
          if (!turno) return "-";
          const escala = escalasAtividade.find(e => e.descricao === turno.escala_ou_atividade);
          return escala?.abreviacao || turno.escala_ou_atividade?.charAt(0) || "-";
        })
      ]);

      // Cores das células por turno
      autoTable(doc, {
        startY: 55,
        head,
        body,
        styles: {
          fontSize: 6,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          lineWidth: 0.3,
          lineColor: [200, 200, 200],
        },
        headStyles: {
          fillColor: [230, 247, 255],
          textColor: [0, 0, 0],
          fontSize: 6,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 80, fontStyle: "bold" },
        },
        didDrawCell: (data) => {
          // Colorir células do corpo (não cabeçalho)
          if (data.section === "body" && data.column.index > 0) {
            const usuario  = usuarios[data.row.index];
            const dia      = dias[data.column.index - 1];
            const turno    = obterTurnoUsuarioDia(usuario.nome, dia.id);
            if (turno?.cor) {
              const hex = turno.cor.replace("#", "");
              const r   = parseInt(hex.substring(0, 2), 16);
              const g   = parseInt(hex.substring(2, 4), 16);
              const b   = parseInt(hex.substring(4, 6), 16);
              doc.setFillColor(r, g, b);
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, "F");
              // Reescreve o texto em branco sobre a cor
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(6);
              doc.text(
                data.cell.text[0] || "",
                data.cell.x + data.cell.width / 2,
                data.cell.y + data.cell.height / 2 + 2,
                { align: "center" }
              );
              doc.setTextColor(0, 0, 0); // restaura
            }
          }
        },
      });

      // Legenda
      const finalY = doc.lastAutoTable.finalY + 20;
      doc.setFontSize(10);
      doc.text("Legenda:", 40, finalY);
      escalasAtividade.forEach((escala, i) => {
        const x = 40 + i * 120;
        const y = finalY + 15;
        const hex = (escala.cor || "#cccccc").replace("#", "");
        doc.setFillColor(
          parseInt(hex.substring(0, 2), 16),
          parseInt(hex.substring(2, 4), 16),
          parseInt(hex.substring(4, 6), 16)
        );
        doc.rect(x, y, 10, 10, "F");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text(`${escala.abreviacao} - ${escala.descricao}`, x + 14, y + 8);
      });

      const fileName = `escala_${mesSelecionado?.descricao.toLowerCase().replace(/ /g, "_")}.pdf`;
      doc.save(fileName);
      sms_success("PDF gerado com sucesso!");
    } catch (err) {
      console.error(err);
      sms_error("Erro ao gerar PDF.");
    } finally {
      setGerandoPDF(false);
    }
  };

  // -------------------------------------------------------
  const fetchMeses = async () => {
    try {
      setLoading(true);
      const response = await api.ListMes();
      setMeses(response || []);
    } catch {
      sms_error("Erro ao carregar meses");
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalasAtividade = async () => {
    try {
      const response = await api.ListEscalaAtividade();
      setEscalasAtividade(response || []);
    } catch {
      sms_error("Erro ao carregar escalas");
    }
  };

  const handleAbrirDetalhes = async (mes) => {
    try {
      setMesSelecionado(mes);
      setDetalhesLoading(true);
      setModalVisible(true);
      const payload = { id_mes: mes.id };
      const [diasResp, turnosResp] = await Promise.all([
        api.ListDia(payload),
        api.ListTurnoTrabalho(payload)
      ]);
      setDias(diasResp || []);
      setTurnos(turnosResp || []);
    } catch {
      sms_error("Erro ao carregar detalhes do mês");
    } finally {
      setDetalhesLoading(false);
    }
  };

  const formatarData = (dataString) => {
    const data = new Date(dataString);
    const dia = data.getDate().toString().padStart(2, "0");
    const diasSemana = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
    return { dia, diaSemana: diasSemana[data.getDay()] };
  };

  const obterDiaPorId       = (idDia)           => dias.find(d => d.id === idDia);
  const obterTurnoUsuarioDia = (nomeUsuario, idDia) =>
    turnos.find(t => t.nome_usuario === nomeUsuario && t.id_dia === idDia);

  const agruparTurnosPorUsuario = () => {
    const map = new Map();
    turnos.forEach(t => {
      if (!map.has(t.nome_usuario)) map.set(t.nome_usuario, { nome: t.nome_usuario });
    });
    return Array.from(map.values());
  };

  const columnsMeses = [
    {
      title: <Space><FaCalendarAlt /><span>Mês</span></Space>,
      dataIndex: "descricao",
      key: "descricao",
      render: (text) => (
        <Space>
          <FaRegCalendarAlt style={{ color: "#1890ff" }} />
          <strong>{text}</strong>
        </Space>
      )
    },
    {
      title: "Ações",
      key: "acoes",
      align: "center",
      render: (_, record) => (
        <Button type="primary" icon={<FaEye />} onClick={() => handleAbrirDetalhes(record)}>
          Ver Escala
        </Button>
      )
    }
  ];

  const renderCelulaDia = (usuario, idDia) => {
    const turno   = obterTurnoUsuarioDia(usuario.nome, idDia);
    const diaInfo = obterDiaPorId(idDia);

    if (!turno || !diaInfo) {
      return (
        <div style={{
          width: "36px", height: "36px", backgroundColor: "#f5f5f5",
          borderRadius: "4px", display: "flex", alignItems: "center",
          justifyContent: "center", margin: "1px auto"
        }}>
          <Text type="secondary" style={{ fontSize: 10 }}>-</Text>
        </div>
      );
    }

    const escala = escalasAtividade.find(e => e.descricao === turno.escala_ou_atividade);

    return (
      <Tooltip title={`${turno.escala_ou_atividade} - ${formatarData(turno.data_completa).diaSemana}`} placement="top">
        <div style={{
          width: "36px", height: "36px",
          backgroundColor: turno.cor,
          borderRadius: "4px", display: "flex",
          alignItems: "center", justifyContent: "center",
          margin: "1px auto", color: "#fff",
          fontWeight: "bold", fontSize: 10, cursor: "pointer"
        }}>
          {escala?.abreviacao || turno.escala_ou_atividade?.charAt(0)}
        </div>
      </Tooltip>
    );
  };

  return (
    <Layout>
      <Content style={{ padding: 24 }}>
        <ToastContainer />

        <Card
          title={
            <Space>
              <FaClock style={{ fontSize: 22, color: "#52c41a" }} />
              <Title level={4} style={{ margin: 0 }}>Gestão de Turnos de Trabalho</Title>
            </Space>
          }
        >
          <Table
            columns={columnsMeses}
            dataSource={meses}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 12 }}
            locale={{ emptyText: <Empty description="Nenhum mês encontrado" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </Card>

        <Modal
          title={<Space><FaCalendarAlt /><span>Escala Mensal - {mesSelecionado?.descricao}</span></Space>}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          width="98%"
          style={{ top: 10 }}
          styles={{ body: { padding: "12px" } }}
          footer={[
            <Button key="pdf" type="primary" icon={<FaFilePdf />} onClick={handleGerarPDF} loading={gerandoPDF}>
              Salvar como PDF
            </Button>,
            <Button key="fechar" onClick={() => setModalVisible(false)}>Fechar</Button>
          ]}
        >
          {detalhesLoading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
              <p>Carregando escala do mês...</p>
            </div>
          ) : (
            <>
              {/* TABELA — sem maxHeight, scroll horizontal apenas */}
              <div style={{ overflowX: "auto", marginBottom: 20 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{
                        position: "sticky", left: 0, zIndex: 2,
                        minWidth: 140, padding: "6px 8px", textAlign: "left",
                        backgroundColor: "#e6f7ff", border: "1px solid #d9d9d9"
                      }}>
                        <Space><FaUser /><span>Colaborador</span></Space>
                      </th>
                      {dias.map(dia => {
                        const { dia: diaNumero, diaSemana } = formatarData(dia.dia);
                        return (
                          <th key={dia.id} style={{
                            padding: "4px 2px", textAlign: "center",
                            minWidth: 42, backgroundColor: "#f0f0f0",
                            border: "1px solid #d9d9d9", fontSize: 10
                          }}>
                            <div style={{ fontSize: 9, color: "#666" }}>{diaSemana}</div>
                            <div style={{ fontWeight: "bold" }}>{diaNumero}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {agruparTurnosPorUsuario().map((usuario, idx) => (
                      <tr key={usuario.nome} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{
                          position: "sticky", left: 0, zIndex: 1,
                          padding: "4px 8px", border: "1px solid #d9d9d9",
                          backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                          fontWeight: 600, fontSize: 11, whiteSpace: "nowrap"
                        }}>
                          <Space>
                            <FaUser style={{ color: "#666", fontSize: 10 }} />
                            {usuario.nome}
                          </Space>
                        </td>
                        {dias.map(dia => (
                          <td key={`${usuario.nome}-${dia.id}`} style={{
                            padding: "2px", border: "1px solid #d9d9d9", textAlign: "center"
                          }}>
                            {renderCelulaDia(usuario, dia.id)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {turnos.length === 0 && (
                  <Empty style={{ margin: "40px 0" }} description="Nenhum turno encontrado para este mês" />
                )}
              </div>

              {/* LEGENDA */}
              <Card title="Legenda de Turnos" size="small">
                <Row gutter={[12, 12]}>
                  {escalasAtividade.map(escala => {
                    const count = turnos.filter(t => t.escala_ou_atividade === escala.descricao).length;
                    return (
                      <Col key={escala.descricao} xs={12} sm={8} md={6} lg={4}>
                        <Space>
                          <div style={{
                            width: 20, height: 20,
                            backgroundColor: escala.cor,
                            borderRadius: 4, flexShrink: 0
                          }} />
                          <div>
                            <Text strong style={{ fontSize: 11 }}>{escala.abreviacao}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 10 }}>{escala.descricao} ({count})</Text>
                          </div>
                        </Space>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            </>
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default TurnosTrabalho;